import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { isNil } from 'ramda';

import { GetBeneficiariesDataDocument } from '../../../../graphql/queries/getBeneficiariesData.gql';
import { UpdatePhysicalPersonLegalBeneficiaryDocument } from '../../graphql/mutations/updatePhysicalPersonLegalBeneficiary.gql';
import Component from './component';
import { SetPrimaryApplicantNumberOfSharesDocument } from './graphql/setPrimaryApplicantNumberOfShares.gql';
import { ManageCurrentUserElementProps } from './types';

const ManageCurrentUserElement: FC<ManageCurrentUserElementProps> = ({
  applicationType,
  companyProfileId,
  getIsAddedAmountSharesValid,
  includeShares,
  isExistingCompany,
  sharesData,
  user,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(
    includeShares && isNil(user?.numberOfShares),
  );

  const [savePrimaryApplicantSharesMutation] = useMutation(
    SetPrimaryApplicantNumberOfSharesDocument,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: { companyProfileId, includeShares: false },
        },
      ],
    },
  );

  const [updateBeneficiaryMutation] = useMutation(
    UpdatePhysicalPersonLegalBeneficiaryDocument,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBeneficiariesDataDocument,
          variables: { companyProfileId, includeShares: false },
        },
      ],
    },
  );

  return (
    <Component
      applicationType={applicationType}
      getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
      includeShares={includeShares}
      isEditing={isEditing}
      isExistingCompany={isExistingCompany}
      loading={isLoading}
      onCardClicked={() => includeShares && !isEditing && setIsEditing(true)}
      onClose={() => {
        setIsEditing(false);
      }}
      onSave={async (editedUser) => {
        if (!user || !user.companyLegalBeneficiaryId) {
          throw new Error(
            "user and user.companyLegalBeneficiaryId can't be undefined when mutating user",
          );
        }

        setIsLoading(true);
        await updateBeneficiaryMutation({
          variables: {
            input: {
              companyLegalBeneficiaryId: user.companyLegalBeneficiaryId,
              companyProfileId,
              isDirector: editedUser.isDirector,
              isMajorShareholder: editedUser.isMajorShareholder,
            },
          },
        });
        await savePrimaryApplicantSharesMutation({
          variables: {
            input: {
              companyProfileId,
              numberOfShares: editedUser.nbShares ?? 0,
            },
          },
        });
        setIsLoading(false);
        setIsEditing(false);
      }}
      sharesData={sharesData}
      user={user}
    />
  );
};

export default ManageCurrentUserElement;
